import { Avatar, Box, Typography } from '@mui/material';
import defaultUserAvatar from '../../assets/default-user-avatar.png';
import subscriptionBadgeSrc from '../../assets/profile-drawer/subscription-badge.png';
import { gameRaphousieShieldSrc, gameRapwheelShieldSrc } from '../../assets/profile';
import { rapcoinCoinSrc, xpCoinSrc } from '../../assets/wallet';
import ContestRibbon from './ContestRibbon';
import RapCardShieldBorder from './RapCardShieldBorder';
import RapCardShieldLogo from './RapCardShieldLogo';

const SHIELD_WIDTH = 400;
const SHIELD_HEIGHT = 749;

const GAME_META = {
  raphousie: {
    icon: gameRaphousieShieldSrc,
    headerBg: 'linear-gradient(180deg, #0048ff 0%, #002b99 100%)',
    headerBorder: '#002b99',
  },
  rapwheel: {
    icon: gameRapwheelShieldSrc,
    headerBg: 'linear-gradient(180deg, #ff00f2 0%, #990091 100%)',
    headerBorder: '#990091',
  },
  rapfire: {
    icon: null,
    headerBg: 'linear-gradient(180deg, #ff3b3b 0%, #a02323 100%)',
    headerBorder: '#a02323',
  },
  rapleague: {
    icon: null,
    headerBg: 'linear-gradient(180deg, #2ee437 0%, #1c8921 100%)',
    headerBorder: '#1c8921',
  },
};

function StatPill({ title, value, icon, headerBg, headerBorder, left }) {
  return (
    <Box sx={{  width: 140, height: 65 }}>
      <Box
        sx={{
         
          width: 140,
          height: 30,
          borderRadius: '8px 8px 0 0',
          border: `1px solid ${headerBorder}`,
          background: headerBg,
          display: 'grid',
          placeItems: 'center',
        }}
      >
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: 14,
            lineHeight: '20px',
            letterSpacing: '0.5px',
            color: '#fff',
          }}
        >
          {title}
        </Typography>
      </Box>
      <Box
        sx={{
         
          width: 140,
          height: 35,
          borderRadius: '0 0 8px 8px',
          bgcolor: 'rgba(0, 0, 0, 0.54)',
          boxShadow: 'inset 0 0 4px rgba(0, 0, 0, 0.65)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '5px',
        }}
      >
        <Box component="img" src={icon} alt="" sx={{ width: 20, height: 20, objectFit: 'contain' }} />
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 600,
            fontSize: 16,
            lineHeight: '24px',
            letterSpacing: '0.8px',
            color: '#fff',
          }}
        >
          {value}
        </Typography>
      </Box>
    </Box>
  );
}

function GameStatCard({ game }) {
  const meta = GAME_META[game.id] || GAME_META.raphousie;

  return (
    <Box sx={{ width: 160, height: 70, position: 'relative' }}>
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '8px',
          bgcolor: 'rgba(0, 0, 0, 0.7)',
          boxShadow: 'inset 0 0 4px rgba(0, 0, 0, 0.65)',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 31,
          borderRadius: '8px 8px 0 0',
          border: `1px solid ${meta.headerBorder}`,
          background: meta.headerBg,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: '6px',
          px: 1,
        }}
      >
        {meta.icon ? (
          <Box component="img" src={meta.icon} alt="" sx={{ width: 20, height: 20, objectFit: 'contain' }} />
        ) : null}
        <Typography
          sx={{
            fontFamily: 'Inter, sans-serif',
            fontWeight: 500,
            fontSize: 14,
            lineHeight: '20px',
            letterSpacing: '0.5px',
            color: '#fff',
            whiteSpace: 'nowrap',
          }}
        >
          {game.label}
        </Typography>
      </Box>
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          top: 31,
          display: 'grid',
          gridTemplateColumns: '1fr 1fr 1fr',
          alignItems: 'center',
          px: 0.5,
        }}
      >
        {[
          { label: 'PLAYED', value: game.played, color: '#ffd700' },
          { label: 'WIN', value: game.win, color: '#2ee437' },
          { label: 'LOSE', value: game.lose, color: '#ff3b3b' },
        ].map((stat, index) => (
          <Box
            key={stat.label}
            sx={{
              textAlign: 'center',
              borderLeft: index === 1 ? '1px solid rgba(255,255,255,0.12)' : 'none',
              borderRight: index === 1 ? '1px solid rgba(255,255,255,0.12)' : 'none',
            }}
          >
            <Typography sx={{ fontSize: 10, lineHeight: '12px', color: 'rgba(255,255,255,0.5)' }}>
              {stat.label}
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: 14,
                lineHeight: '17px',
                color: stat.color,
                letterSpacing: '0.8px',
              }}
            >
              {stat.value}
            </Typography>
          </Box>
        ))}
      </Box>
    </Box>
  );
}

/** Figma 11059:2017 — RapCard shield with logo 11059:2089 */
export default function RapCardShield({ profile }) {
  const ratioPercent = Math.max(0, Math.min(100, profile.winningRatio));
  const progressWidth = `${(320 * ratioPercent) / 100}px`;

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: SHIELD_WIDTH,
        height: SHIELD_HEIGHT,
        mx: 'auto',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          inset: 0,
          pointerEvents: 'none',
          zIndex: 0,
        }}
      >
        <RapCardShieldBorder />
      </Box>

      <Box sx={{ position: 'relative', zIndex: 1, width: '100%', height: '100%' }}>
        <RapCardShieldLogo />
        <Box sx={{
            position: 'absolute',
            top: '125px',
            display: 'flex',
            justifyContent: 'space-around',
            width: '100%',
          }}>
          <StatPill
            title="Winning Points"
            value={profile.winningPoints}
            icon={rapcoinCoinSrc}
            headerBg="linear-gradient(180deg, #5d96ff 0%, #0023ad 100%)"
            headerBorder="#00266c"
            left="30px"
          />
          <StatPill
            title="Earned XPs"
            value={profile.earnedXp}
            icon={xpCoinSrc}
            headerBg="linear-gradient(180deg, #ffda5d 0%, #ad8500 100%)"
            headerBorder="#6c4f00"
            left="190px"
          />

        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: '187.29px',
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <Avatar
            src={profile.avatarSrc || defaultUserAvatar}
            alt={profile.displayName}
            sx={{
              width: 60,
              height: 60,
              border: '2px solid #ffd700',
              bgcolor: '#ffceca',
              '& .MuiAvatar-img': { objectFit: 'cover', objectPosition: 'center top' },
            }}
          />
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: '260.18px',
            left: '50%',
            transform: 'translateX(-50%)',
            display: 'flex',
            alignItems: 'center',
            gap: '5px',
            whiteSpace: 'nowrap',
          }}
        >
          <Typography
            sx={{
              fontFamily: 'Inter, sans-serif',
              fontWeight: 700,
              fontSize: 20,
              lineHeight: '32px',
              letterSpacing: '0.8px',
              color: '#fff',
            }}
          >
            {profile.displayName}
          </Typography>
          <Box sx={{ position: 'relative', width: 28, height: 28, overflow: 'hidden', flexShrink: 0 }}>
            <Box
              component="img"
              src={subscriptionBadgeSrc}
              alt=""
              sx={{
                position: 'absolute',
                height: '99.65%',
                width: '149.34%',
                left: '-24.67%',
                top: '7.64%',
              }}
            />
          </Box>
        </Box>

        <Box
          sx={{
            position: 'absolute',
            top: '307.7px',
            left: '30px',
            width: 340,
            height: 57,
            borderRadius: '20px 20px 0 0',
            bgcolor: '#180040',
            boxShadow: 'inset 0 4px 4px rgba(0, 0, 0, 0.55)',
            px: '10px',
            pt: '10px',
            pb: '9px',
            boxSizing: 'border-box',
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: '5px' }}>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: 16,
                lineHeight: '24px',
                color: '#fff',
              }}
            >
              Winning Ratio
            </Typography>
            <Typography
              sx={{
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                fontSize: 16,
                lineHeight: '24px',
                color: '#2ee437',
              }}
            >
              {profile.winningRatio}%
            </Typography>
          </Box>
          <Box sx={{ position: 'relative', height: 8, borderRadius: 999, bgcolor: 'rgba(255,255,255,0.5)' }}>
            <Box
              sx={{
                position: 'absolute',
                left: 0,
                top: 0,
                height: '100%',
                width: progressWidth,
                maxWidth: '100%',
                bgcolor: '#2ee437',
                borderRadius: 999,
              }}
            />
          </Box>
        </Box>

        <ContestRibbon
          totalContests={profile.totalContests}
          totalWins={profile.totalWins}
          totalLosses={profile.totalLosses}
        />

        <Box
          sx={{
            position: 'absolute',
            top: '461.6px',
            left: '30px',
            width: 340,
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: '20px 20px',
          }}
        >
          {profile.gameStats.map((game) => (
            <GameStatCard key={game.id} game={game} />
          ))}
        </Box>

        <Box
          component="button"
          type="button"
          aria-label="Share RapCard"
          sx={{
            position: 'absolute',
            bottom: '15.74px',
            left: '50%',
            transform: 'translateX(-50%)',
            width: 54,
            height: 54,
            borderRadius: '50%',
            border: '1px solid transparent',
            bgcolor: '#180040',
            boxShadow: 'inset 0 4px 4px rgba(0, 0, 0, 0.55)',
            cursor: 'pointer',
            display: 'grid',
            placeItems: 'center',
            visibility: 'hidden',
            p: '5px',
            '&:hover': { opacity: 0.92 },
          }}
        >
          <Box
            component="span"
            sx={{
              width: 20,
              height: 20,
              borderTop: '2px solid #009dc9',
              borderRight: '2px solid #009dc9',
              transform: 'rotate(45deg) translate(-2px, 2px)',
              
            }}
          />
        </Box>
      </Box>
    </Box>
  );
}
